import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { TopBar } from '@/components/TopBar';
import type { NostrProfile } from '@/hooks/useNostrLogin';

const EditorPage = lazy(() => import('./pages/EditorPage').then((m) => ({ default: m.EditorPage })));
const RemixPage = lazy(() => import('./pages/RemixPage').then((m) => ({ default: m.RemixPage })));
const BrowsePage = lazy(() => import('./pages/BrowsePage').then((m) => ({ default: m.BrowsePage })));
const TileDetailPage = lazy(() => import('./pages/TileDetailPage').then((m) => ({ default: m.TileDetailPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  );
}

interface AppRouterProps {
  pubkey: string | null;
  profile: NostrProfile | null;
  isLoading: boolean;
  isExtensionAvailable: boolean;
  relays: string[];
  onLoginExtension: () => Promise<void>;
  onLoginNsec: (nsec: string) => Promise<void>;
  onLogout: () => void;
  onSetRelays: (relays: string[]) => void;
  onRefreshProfile: () => Promise<void>;
}

export function AppRouter({
  pubkey,
  profile,
  isLoading,
  isExtensionAvailable,
  relays,
  onLoginExtension,
  onLoginNsec,
  onLogout,
  onSetRelays,
}: AppRouterProps) {
  const isLoggedIn = !!pubkey;
  const hasNip05 = !!profile?.nip05;

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Editor — full-screen, no top bar */}
          <Route
            path="/editor"
            element={
              isLoggedIn && hasNip05 ? (
                <EditorPage
                  pubkey={pubkey!}
                  nip05={profile!.nip05!}
                  relays={relays}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/remix/:naddr"
            element={
              isLoggedIn && hasNip05 ? (
                <RemixPage
                  pubkey={pubkey!}
                  relays={relays}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Settings */}
          <Route
            path="/settings"
            element={
              isLoggedIn ? (
                <SettingsPage
                  pubkey={pubkey!}
                  profile={profile}
                  relays={relays}
                  onSetRelays={onSetRelays}
                  onLogout={onLogout}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* Pages with TopBar */}
          <Route
            path="*"
            element={
              <WithTopBar
                pubkey={pubkey}
                profile={profile}
                isLoading={isLoading}
                isExtensionAvailable={isExtensionAvailable}
                onLoginExtension={onLoginExtension}
                onLoginNsec={onLoginNsec}
                onLogout={onLogout}
              >
                <Routes>
                  <Route
                    path="/"
                    element={<BrowsePage relays={relays} />}
                  />
                  <Route
                    path="/tile/:naddr"
                    element={<TileDetailPage relays={relays} pubkey={pubkey} profile={profile} />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </WithTopBar>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/** Layout wrapper that renders TopBar + children */
function WithTopBar({
  children,
  pubkey,
  profile,
  isLoading,
  isExtensionAvailable,
  onLoginExtension,
  onLoginNsec,
  onLogout,
}: {
  children: React.ReactNode;
  pubkey: string | null;
  profile: NostrProfile | null;
  isLoading: boolean;
  isExtensionAvailable: boolean;
  onLoginExtension: () => Promise<void>;
  onLoginNsec: (nsec: string) => Promise<void>;
  onLogout: () => void;
}) {
  return (
    <div className="min-h-screen flex flex-col">
      <TopBar
        pubkey={pubkey}
        profile={profile}
        isLoading={isLoading}
        isExtensionAvailable={isExtensionAvailable}
        onLoginExtension={onLoginExtension}
        onLoginNsec={onLoginNsec}
        onLogout={onLogout}
      />
      <main className="flex-1">{children}</main>
    </div>
  );
}
